<template>
  <div class="renew-page">
    <!-- 顶部导航 -->
    <div class="nav-header">
      <button class="back-btn" @click="$emit('close')">
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path fill="#333" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
        </svg>
      </button>
      <span class="nav-title">账户续费</span>
      <div class="nav-right"></div>
    </div>

    <!-- 账户信息卡片 -->
    <div class="info-card">
      <div class="info-header">
        <div class="info-item">
          <span class="info-label">当前余额</span>
          <span class="info-value">{{ userInfo.balance }} 元</span>
        </div>
        <div class="info-item">
          <span class="info-label">到期状态</span>
          <span class="info-value" :class="expireClass">{{ expireText }}</span>
        </div>
      </div>
    </div>

    <!-- 套餐选择 -->
    <div class="section-title">选择续费套餐</div>
    <div class="packages-list">
      <div
        v-for="pkg in packages"
        :key="pkg.id"
        class="package-card"
        :class="{ selected: selectedPackage === pkg.id }"
        @click="selectPackage(pkg.id)"
      >
        <div class="package-badge" v-if="pkg.recommended">推荐</div>
        <div class="package-name">{{ pkg.name }}</div>
        <div class="package-price">
          <span class="price-value">{{ pkg.price }}</span>
          <span class="price-unit">元</span>
        </div>
        <div class="package-days">{{ pkg.days }}天</div>
        <div class="package-original" v-if="pkg.originalPrice">
          原价 ¥{{ pkg.originalPrice }}
        </div>
      </div>
    </div>

    <!-- 底部操作区 -->
    <div class="action-section">
      <div class="summary" v-if="selectedPackageData">
        <span>已选：{{ selectedPackageData.name }}</span>
        <span class="summary-price">¥{{ selectedPackageData.price }}</span>
      </div>
      <button
        class="confirm-btn"
        :disabled="!canPurchase || purchasing"
        @click="confirmPurchase"
      >
        {{ purchasing ? '处理中...' : (canPurchase ? '立即续费' : '余额不足') }}
      </button>
      <div class="recharge-hint" v-if="!canPurchase && selectedPackageData">
        余额不足，<span class="link" @click="goRecharge">去充值</span>
      </div>
    </div>

    <!-- 成功弹窗 -->
    <div class="success-modal" v-if="showSuccess">
      <div class="success-content">
        <div class="success-icon">✓</div>
        <h3>续费成功</h3>
        <p>已续费 {{ purchaseResult.packageName }}（{{ purchaseResult.renewDays }}天）</p>
        <p>新到期时间：{{ formatExpireTime(purchaseResult.newExpireTime) }}</p>
        <p>剩余余额：¥{{ purchaseResult.newBalance }}</p>
        <button class="success-btn" @click="closeSuccess">确定</button>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/config/api'

export default {
  name: 'RenewPage',
  data() {
    return {
      userInfo: {
        balance: 0,
        expireDays: 0,
        expireTime: null
      },
      packages: [],
      selectedPackage: null,
      purchasing: false,
      showSuccess: false,
      purchaseResult: null
    }
  },
  computed: {
    selectedPackageData() {
      return this.packages.find(p => p.id === this.selectedPackage)
    },
    canPurchase() {
      if (!this.selectedPackageData) return false
      return this.userInfo.balance >= this.selectedPackageData.price
    },
    expireText() {
      if (this.userInfo.expireDays >= 999) return '永久有效'
      if (this.userInfo.expireDays > 0) return `剩余 ${this.userInfo.expireDays} 天`
      if (this.userInfo.expireDays === 0) return '今天到期'
      return `已过期 ${Math.abs(this.userInfo.expireDays)} 天`
    },
    expireClass() {
      if (this.userInfo.expireDays >= 999) return 'expire-forever'
      if (this.userInfo.expireDays > 7) return 'expire-normal'
      if (this.userInfo.expireDays > 0) return 'expire-warning'
      return 'expire-danger'
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      try {
        const [userRes, packagesRes] = await Promise.all([
          apiFetch('/api/renew/user-info'),
          apiFetch('/api/renew/packages')
        ])

        if (userRes.code === 0 && userRes.data) {
          this.userInfo = {
            balance: parseFloat(userRes.data.balance) || 0,
            expireDays: userRes.data.expireDays,
            expireTime: userRes.data.expireTime
          }
        }

        if (packagesRes.code === 0 && packagesRes.data) {
          this.packages = packagesRes.data
          const recommended = this.packages.find(p => p.recommended)
          if (recommended) {
            this.selectedPackage = recommended.id
          } else if (this.packages.length > 0) {
            this.selectedPackage = this.packages[0].id
          }
        }
      } catch (e) {
        console.error('加载数据失败', e)
      }
    },
    selectPackage(id) {
      this.selectedPackage = id
    },
    async confirmPurchase() {
      if (!this.canPurchase || this.purchasing) return

      if (!confirm(`确认花费 ¥${this.selectedPackageData.price} 续费 ${this.selectedPackageData.name}？`)) {
        return
      }

      this.purchasing = true
      try {
        const res = await apiFetch('/api/renew/purchase', {
          method: 'POST',
          body: JSON.stringify({
            packageId: this.selectedPackage
          })
        })

        if (res.code === 0 && res.data) {
          this.purchaseResult = res.data
          this.showSuccess = true
          this.userInfo.balance = parseFloat(res.data.newBalance) || 0
          this.userInfo.expireDays = res.data.newExpireDays
          this.userInfo.expireTime = res.data.newExpireTime
        } else {
          alert(res.msg || '续费失败')
        }
      } catch (e) {
        console.error('续费失败', e)
        alert('续费失败，请重试')
      } finally {
        this.purchasing = false
      }
    },
    formatExpireTime(timeStr) {
      if (!timeStr) return ''
      const date = new Date(timeStr)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    },
    closeSuccess() {
      this.showSuccess = false
      this.$emit('close')
    },
    goRecharge() {
      this.$emit('go-recharge')
    }
  }
}
</script>

<style scoped>
.renew-page {
  min-height: 100%;
  height: 100%;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.nav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #eee;
}

.back-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
}

.nav-title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
}

.nav-right { width: 32px; }

/* 账户信息卡片 */
.info-card {
  margin: 16px;
  background: linear-gradient(135deg, #1677ff 0%, #0958d9 100%);
  border-radius: 12px;
  overflow: hidden;
}

.info-header {
  padding: 20px;
  color: #fff;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.info-label {
  font-size: 14px;
  opacity: 0.9;
}

.info-value {
  font-size: 16px;
  font-weight: 500;
}

.expire-forever { color: #90EE90; }
.expire-normal { color: #fff; }
.expire-warning { color: #FFD700; }
.expire-danger { color: #FF6B6B; }

/* 套餐选择 */
.section-title {
  padding: 0 16px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.packages-list {
  display: flex;
  gap: 12px;
  padding: 0 16px;
  overflow-x: auto;
}

.package-card {
  flex: 1;
  min-width: 100px;
  background: #fff;
  border-radius: 12px;
  padding: 16px 12px;
  text-align: center;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.package-card.selected {
  border-color: #1677ff;
  background: #f0f7ff;
}

.package-badge {
  position: absolute;
  top: 0;
  right: 0;
  background: #ff9800;
  color: #fff;
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 0 10px 0 8px;
}

.package-name {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.package-price {
  margin-bottom: 4px;
}

.price-value {
  font-size: 28px;
  font-weight: 700;
  color: #1677ff;
}

.price-unit {
  font-size: 14px;
  color: #666;
}

.package-days {
  font-size: 12px;
  color: #999;
}

.package-original {
  font-size: 12px;
  color: #999;
  text-decoration: line-through;
  margin-top: 4px;
}

/* 底部操作区 */
.action-section {
  margin-top: auto;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  background: #fff;
  border-top: 1px solid #eee;
}

.summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  color: #666;
}

.summary-price {
  font-size: 20px;
  font-weight: 700;
  color: #1677ff;
}

.confirm-btn {
  width: 100%;
  height: 48px;
  background: #1677ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 17px;
  font-weight: 500;
  cursor: pointer;
}

.confirm-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.recharge-hint {
  text-align: center;
  margin-top: 12px;
  font-size: 14px;
  color: #999;
}

.recharge-hint .link {
  color: #1677ff;
  cursor: pointer;
}

/* 成功弹窗 */
.success-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.success-content {
  background: #fff;
  border-radius: 12px;
  padding: 30px;
  width: 280px;
  text-align: center;
}

.success-icon {
  width: 60px;
  height: 60px;
  background: #52c41a;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  color: #fff;
}

.success-content h3 {
  margin: 0 0 16px;
  font-size: 20px;
  color: #333;
}

.success-content p {
  margin: 8px 0;
  font-size: 14px;
  color: #666;
}

.success-btn {
  width: 100%;
  height: 44px;
  background: #1677ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 20px;
}
</style>
